import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MapPin, Clock, Check, DollarSign } from "lucide-react";
import { PremiumEventBadges } from "./PremiumEventBadges";
import { formatInCentralTime } from "@/utils/timezone";

interface Event {
  id: string;
  title: string;
  date: string;
  location: string;
  category: string;
  host_name: string;
  duration_min?: number;
  description?: string;
  time_of_day?: string;
  featured?: boolean;
  is_free?: boolean;
  is_cowgirls_hosted?: boolean;
  premium_included?: boolean;
  premium_discount_percent?: number;
  early_access_hours?: number;
}

interface CalendarEventCardProps {
  event: Event;
  isRsvpd: boolean;
  onRsvp: () => void;
}

export const CalendarEventCard = ({ event, isRsvpd, onRsvp }: CalendarEventCardProps) => {
  const eventTime = formatInCentralTime(event.date, 'h:mm a');
  const hasPremiumBenefits = event.is_cowgirls_hosted || event.premium_included || 
    (event.premium_discount_percent && event.premium_discount_percent > 0) || 
    (event.early_access_hours && event.early_access_hours > 0);

  return (
    <Card className="p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 space-y-2">
          <div className="flex items-start gap-2">
            <div className="flex-1">
              <h4 className="font-semibold text-sm line-clamp-1">
                {event.featured && <span className="mr-1">⭐</span>}
                {!event.is_free && <DollarSign className="inline h-3 w-3 text-primary mr-1" />}
                {event.title}
              </h4>
              <p className="text-xs text-muted-foreground">{event.host_name}</p>
            </div>
            <Badge variant="secondary" className="text-xs flex-shrink-0">
              {event.category}
            </Badge>
          </div>

          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <div className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              <span>{eventTime}</span>
              {event.duration_min && (
                <span className="text-muted-foreground/60">
                  · {event.duration_min} min
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              <span className="line-clamp-1">{event.location}</span>
            </div>
          </div>

          {hasPremiumBenefits && (
            <PremiumEventBadges
              isCowgirlsHosted={event.is_cowgirls_hosted}
              isPremiumIncluded={event.premium_included}
              premiumDiscountPercent={event.premium_discount_percent}
              earlyAccessHours={event.early_access_hours}
              compact
            />
          )}
        </div>

        <Button
          size="sm"
          variant={isRsvpd ? "secondary" : "default"}
          onClick={onRsvp}
          className="flex-shrink-0"
        >
          {isRsvpd ? (
            <>
              <Check className="h-3 w-3 mr-1" />
              RSVP'd
            </>
          ) : (
            'RSVP'
          )}
        </Button>
      </div>
    </Card>
  );
};
